import { ClientGroupType } from "src/generated/graphql";
import { useForm, yupResolver } from "@mantine/form";
import SegmentedControl from "src/components/SegmentedControl";
import {
  Button,
  Text,
  Group,
  useMantineTheme,
  SelectItem,
  Flex,
} from "@mantine/core";
import Select from "src/components/Select";
import { useState, useEffect } from "react";
import {
  useCreateClientGroupMutation,
  useAddClientToGroupMutation,
  CreateClientGroupInput,
  GetClientGroupPageQuery,
  GetClientGroupPageQueryVariables,
} from "src/generated/graphql";
import TextInput from "src/components/TextInput/TextInput";
import { debounce } from "src/utils/utils";
import { useApolloClient } from "@apollo/client";
import {
  GetClientGroupMany,
  GetClientGroupPage,
} from "src/api/graphql/queries/Client";
import { object, string } from "yup";

interface CreateClientGroupProps<T> {
  handleClose: () => void;
  clientId?: string;
  clientName?: string;
  refetchClientGroups?: () => void;
}

type IFormProps = Omit<CreateClientGroupInput, "primaryClient"> & {
  name: string;
  primaryClient: string;
  clientGroupType: ClientGroupType;
};

const clientGroupInitialValues = {
  name: "",
  primaryClient: "",
  clientGroupType: ClientGroupType.Product,
};

const clientGroupFormSchema = object().shape({
  name: string().required("Group name is required"),
});

export enum FormTypes {
  NewGroup = "New Group",
  ExistingGroup = "Existing Group",
}

export const CreateClientGroupForm = <T,>({
  clientId,
  clientName,
  handleClose,
}: CreateClientGroupProps<T>) => {
  const theme = useMantineTheme();
  const { query } = useApolloClient();
  const [formType, setFormType] = useState<FormTypes>(FormTypes.NewGroup);
  const [searchedTypeList, setSearchTypeList] = useState<SelectItem[]>([]);
  const [searchedClientGroup, setSearchedClientGroup] = useState<string | null>(
    ""
  );
  const [searchedValue, setSearchedValue] = useState("");

  const handleFormChange = (e: FormTypes) => {
    setFormType(e);
  };
  const form = useForm<IFormProps>({
    initialValues: clientGroupInitialValues,
    validate:
      formType === FormTypes.NewGroup
        ? yupResolver(clientGroupFormSchema)
        : yupResolver(object()),
  });
  const refetchQuery = [
    { query: GetClientGroupMany, variables: { id: clientId } },
  ];

  const [createClientGroup, { loading: createLoading }] =
    useCreateClientGroupMutation({
      onCompleted: () => {
        handleClose();
      },
      refetchQueries: refetchQuery,
    });

  const [addClientToGroup, { loading: updateLoading }] =
    useAddClientToGroupMutation({
      onCompleted: () => {
        handleClose();
      },
      refetchQueries: refetchQuery,
    });

  const handleSubmit = (values: IFormProps) => {
    if (clientId) {
      formType === FormTypes.NewGroup &&
        createClientGroup({
          variables: {
            data: {
              ...values,
              primaryClient: { id: clientId },
            },
          },
        });
      if (searchedClientGroup) {
        formType === FormTypes.ExistingGroup &&
          addClientToGroup({
            variables: {
              clientId,
              clientGroupId: searchedClientGroup,
            },
          });
      }
    }
  };

  const onSearchList = (searchValue: string, isFirstTime = false) => {
    setSearchedValue(searchValue);
    // Checking if selected id exists in the previous search item list
    const isSameItem = searchedTypeList.some(
      (searchListItem) => searchListItem.value === searchedClientGroup
    );
    if ((!searchValue && !isFirstTime) || isSameItem) {
      return;
    }
    const variables = {
      pagination: {
        count: 10,
        page: 1,
        filters: [
          {
            field: "name",
            type: "text",
            value: searchValue,
          },
        ],
      },
    };

    query<GetClientGroupPageQuery, GetClientGroupPageQueryVariables>({
      query: GetClientGroupPage,
      variables,
    }).then(({ data }) => {
      const list = data?.clientGroupPage?.items?.map((val) => {
        return {
          label: val?.name,
          value: val?.id,
        };
      });
      setSearchTypeList(list || []);
    });
  };

  const onDebouncedSearch = debounce(onSearchList, 500);

  useEffect(() => {
    onSearchList("", true);
  }, []);

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Flex sx={{ alignItems: "center", justifyContent: "center" }}>
        <SegmentedControl
          data={[FormTypes.NewGroup, FormTypes.ExistingGroup]}
          value={formType}
          onChange={handleFormChange}
        />
      </Flex>

      {formType === FormTypes.NewGroup ? (
        <>
          <Group my={10} px={15} position="apart">
            <Text fw="bold" fs="10">
              Primary Account :
            </Text>

            <Text>{clientName}</Text>
          </Group>

          <TextInput
            withAsterisk
            label="Group Name"
            {...form.getInputProps("name")}
          />
        </>
      ) : (
        <Select
          data={searchedTypeList || []}
          placeholder={"Search"}
          searchable
          clearable
          maxDropdownHeight={500}
          nothingFound={
            searchedValue ? "No result found!" : "Please type something!"
          }
          onSearchChange={onDebouncedSearch}
          onChange={setSearchedClientGroup}
        />
      )}

      <Group position="apart" mt="md" style={{ marginTop: 30 }}>
        <Button
          radius="md"
          variant="outline"
          onClick={handleClose}
          style={{
            flex: 1,
            color: theme.colors.secondaryTextColor[6],
          }}
          sx={{
            backgroundColor: theme.colors.secondary[6],
            "&:hover": {
              background: theme.colors.secondary[5],
            },
          }}
        >
          Cancel
        </Button>
        <Button
          radius="md"
          type="submit"
          disabled={createLoading || updateLoading}
          style={{
            flex: 1,
          }}
          sx={{
            color: theme.colors.primaryTextColor[6],
            "&:hover": {
              backgroundColor: theme.colors.primary[4],
            },
          }}
          loading={createLoading || updateLoading}
        >
          Add
        </Button>
      </Group>
    </form>
  );
};
